// ============================================================================
// TYPY
// ============================================================================

interface AnimatedWebpConfig {
  threshold: number;
  rootMargin: string;
  zoneStart: number;
  zoneEnd: number;
}

interface AnimatedWebpInstance {
  destroy: () => void;
  updateConfig: (newConfig: Partial<AnimatedWebpConfig>) => void;
  updateVisibleElement: () => void;
}

interface ZoneCheckResult {
  isInZone: boolean;
  distance: number;
  isFullyVisibleHorizontally: boolean;
}

// ============================================================================
// KONSTANTY
// ============================================================================

const CONSTANTS = {
  MOBILE_BREAKPOINT: 679,
  MENU_OFFSET: 68,
  IMAGE_LOAD_TIMEOUT: 2000,
  VISIBILITY_PENALTY: 100000,
  HORIZONTAL_WEIGHT: 10,
} as const;

const SELECTORS = {
  animatedWebp: '.global-image__animated-webp',
  staticImage: '.global-image__img--has-animated-webp',
  container: '.global-image__container--has-animated-webp',
  parentCard: '.article-card__container, .video-card',
} as const;

const DEFAULT_CONFIG: AnimatedWebpConfig = {
  threshold: 1.0,
  rootMargin: '-80px 0px 0px 0px',
  zoneStart: 0.15,
  zoneEnd: 0.85,
};

// ============================================================================
// CACHE & STATE
// ============================================================================

// Cache pro sdílený blob (původní soubor)
const sharedBlobCache = new Map<string, Blob>();
// Cache pro individuální blob URLs pro každý container
const containerBlobUrls = new WeakMap<HTMLElement, string>();
const conversionQueue = new Map<string, Promise<Blob>>();
const showQueue = new Map<HTMLElement, Promise<void>>();
const visibilityState = new WeakMap<HTMLElement, boolean>();
const operationIds = new WeakMap<HTMLElement, number>();
let nextOperationId = 0;

// ============================================================================
// UTILITY FUNKCE
// ============================================================================

const detectMobile = (): boolean => window.innerWidth <= CONSTANTS.MOBILE_BREAKPOINT;
const detectLandscape = (): boolean => window.innerWidth > window.innerHeight;
const isFirefox = (): boolean => navigator.userAgent.includes('Firefox');

function getOriginalSrc(img: HTMLImageElement): string {
  if (img.dataset.originalSrc) {
    return img.dataset.originalSrc;
  }

  if (img.src && !img.src.startsWith('blob:')) {
    img.dataset.originalSrc = img.src;
    return img.src;
  }

  const attrSrc = img.getAttribute('src') || '';
  if (!attrSrc) return '';

  try {
    const absoluteSrc = new URL(attrSrc, window.location.href).href;
    img.dataset.originalSrc = absoluteSrc;
    return absoluteSrc;
  } catch (error) {
    console.error('Failed to parse URL:', attrSrc, error);
    return '';
  }
}

function waitForImageLoad(img: HTMLImageElement): Promise<void> {
  return new Promise<void>((resolve) => {
    if (img.complete && img.naturalWidth > 0) {
      resolve();
      return;
    }

    const handleLoad = () => {
      img.removeEventListener('load', handleLoad);
      resolve();
    };

    img.addEventListener('load', handleLoad);

    setTimeout(() => {
      img.removeEventListener('load', handleLoad);
      resolve();
    }, CONSTANTS.IMAGE_LOAD_TIMEOUT);
  });
}

// ============================================================================
// NAHRÁNÍ OBRÁZKU A KONVERZE DO BLOB
// ============================================================================

async function loadImageAsBlob(originalSrc: string): Promise<Blob> {
  const cached = sharedBlobCache.get(originalSrc);
  if (cached) {
    return cached;
  }

  const queued = conversionQueue.get(originalSrc);
  if (queued) {
    return queued;
  }

  const conversionPromise = (async () => {
    try {
      const response = await fetch(originalSrc);
      const blob = await response.blob();

      sharedBlobCache.set(originalSrc, blob);
      return blob;
    } catch (error) {
      console.error('Failed to load image as blob:', error);
      throw error;
    } finally {
      conversionQueue.delete(originalSrc);
    }
  })();

  conversionQueue.set(originalSrc, conversionPromise);
  return conversionPromise;
}

// Vytvoří unikátní blob URL pro daný container
function getOrCreateContainerBlobUrl(container: HTMLElement, blob: Blob): string {
  const existing = containerBlobUrls.get(container);
  if (existing) {
    return existing;
  }

  const blobUrl = URL.createObjectURL(blob);
  containerBlobUrls.set(container, blobUrl);
  return blobUrl;
}

async function resetAnimation(
  img: HTMLImageElement,
  blobUrl: string,
  container: HTMLElement,
  operationId: number
): Promise<boolean> {
  // Kontrola před resetem
  if (operationIds.get(container) !== operationId) {
    return false;
  }

  img.src = '';
  img.removeAttribute('src');

  await new Promise((resolve) => setTimeout(resolve, 50));

  // Kontrola po timeoutu
  if (operationIds.get(container) !== operationId) {
    return false;
  }

  img.src = blobUrl;
  img.classList.remove('global-image__animated-webp--fallback');

  // Vynucení reflow
  void img.offsetHeight;

  await waitForImageLoad(img);

  // Finální kontrola
  return operationIds.get(container) === operationId && visibilityState.get(container) === true;
}

// ============================================================================
// VIEWPORT VÝPOČTY
// ============================================================================

function isInCenterZone(
  rect: DOMRect,
  viewportWidth: number,
  viewportHeight: number,
  config: AnimatedWebpConfig
): ZoneCheckResult {
  const menuOffset = CONSTANTS.MENU_OFFSET;
  const verticalUpperBound = viewportHeight * config.zoneStart + menuOffset;
  const verticalLowerBound = viewportHeight * config.zoneEnd;
  const viewportVerticalMiddle = viewportHeight / 2;
  const containerVerticalMiddle = rect.top + rect.height / 2;

  const isVerticallyVisible = rect.bottom > 0 && rect.top < viewportHeight;
  const isInVerticalZone =
    isVerticallyVisible &&
    containerVerticalMiddle >= verticalUpperBound &&
    containerVerticalMiddle <= verticalLowerBound;

  const isHorizontallyVisible = rect.right > 0 && rect.left < viewportWidth;
  const isFullyVisibleHorizontally = rect.left >= 0 && rect.right <= viewportWidth;

  const isInZone = isInVerticalZone && isHorizontallyVisible;

  const verticalDistance = Math.abs(containerVerticalMiddle - viewportVerticalMiddle);
  const leftPosition = Math.max(0, rect.left);
  const visibilityPenalty = isFullyVisibleHorizontally ? 0 : CONSTANTS.VISIBILITY_PENALTY;
  const distance =
    visibilityPenalty + leftPosition * CONSTANTS.HORIZONTAL_WEIGHT + verticalDistance;

  return { isInZone, distance, isFullyVisibleHorizontally };
}

function findClosestContainer(
  containers: HTMLElement[],
  viewportWidth: number,
  viewportHeight: number,
  config: AnimatedWebpConfig
): HTMLElement | null {
  let closestContainer: HTMLElement | null = null;
  let closestDistance = Infinity;

  for (const container of containers) {
    const rect = container.getBoundingClientRect();
    const { isInZone, distance } = isInCenterZone(rect, viewportWidth, viewportHeight, config);

    if (isInZone && distance < closestDistance) {
      closestDistance = distance;
      closestContainer = container;
    }
  }

  return closestContainer;
}

// ============================================================================
// LOGIKA ZOBRAZIT/SKRÝT OBRÁZEK
// ============================================================================

function hideImage(img: HTMLImageElement, container: HTMLElement): void {
  img.style.opacity = '0';
  visibilityState.set(container, false);
  // Invalidujeme běžící operaci přiřazením nového ID
  operationIds.set(container, nextOperationId++);
}

async function showImage(
  img: HTMLImageElement,
  container: HTMLElement,
  currentlyAnimatedElement: HTMLElement | null
): Promise<void> {
  if (showQueue.has(container)) {
    return;
  }

  const wasHidden = visibilityState.get(container) === false;
  const isAlreadyVisible = currentlyAnimatedElement === container && img.style.opacity === '1';

  if (isAlreadyVisible && !wasHidden) {
    return;
  }

  visibilityState.set(container, true);
  const operationId = nextOperationId++;
  operationIds.set(container, operationId);

  const showPromise = (async () => {
    try {
      const originalSrc = getOriginalSrc(img);
      if (!originalSrc) return;

      // Spustíme načtení blobu na pozadí, pokud ještě neběží
      if (!sharedBlobCache.has(originalSrc) && !conversionQueue.has(originalSrc)) {
        void loadImageAsBlob(originalSrc);
      }

      const isFirstShow = !container.dataset.hasBeenShown;

      img.style.opacity = '0';

      // Kontrola, zda je tato operace stále aktuální
      if (operationIds.get(container) !== operationId) return;

      let shouldShow = true;

      // Počkáme na blob (ať už je v cache nebo se právě načítá)
      try {
        const blob = await loadImageAsBlob(originalSrc);

        // Kontrola po načtení blobu
        if (operationIds.get(container) !== operationId) return;

        let containerBlobUrl: string;

        if (isFirstShow) {
          // První zobrazení - vytvoříme blob URL
          containerBlobUrl = getOrCreateContainerBlobUrl(container, blob);
        } else {
          // Opakované zobrazení
          if (isFirefox()) {
            // Pro Firefox vytvoříme vždy NOVÝ blob URL, aby se reset animace provedl správně
            const oldUrl = containerBlobUrls.get(container);
            if (oldUrl) {
              URL.revokeObjectURL(oldUrl);
            }
            containerBlobUrl = URL.createObjectURL(blob);
            containerBlobUrls.set(container, containerBlobUrl);
          } else {
            // Pro ostatní prohlížeče používáme stejný blob URL
            containerBlobUrl = getOrCreateContainerBlobUrl(container, blob);
          }
        }

        if (isFirstShow) {
          // Poprvé nastavíme src a počkáme na načtení
          img.src = containerBlobUrl;
          await waitForImageLoad(img);

          // Označíme jako zobrazené hned po prvním pokusu o načtení
          container.dataset.hasBeenShown = 'true';

          shouldShow =
            operationIds.get(container) === operationId && visibilityState.get(container) === true;
        } else {
          // Opakované zobrazení - reset animace se stejným (ale unikátním) blob URL
          shouldShow = await resetAnimation(img, containerBlobUrl, container, operationId);
        }
      } catch (error) {
        console.error('Failed to load blob for animation:', error);
        return;
      }

      // Pokud během načítání už nemá být viditelný, skončíme
      if (!shouldShow) {
        img.style.opacity = '0';
        return;
      }

      if (!img.classList.contains('global-image__animated-webp--loaded')) {
        img.classList.add('global-image__animated-webp--loaded');
      }

      // Skrytí předchozího animovaného prvku (pokud je v mobilním režimu)
      if (currentlyAnimatedElement && currentlyAnimatedElement !== container) {
        const prevImg = currentlyAnimatedElement.querySelector(
          SELECTORS.animatedWebp
        ) as HTMLImageElement;
        if (prevImg) {
          hideImage(prevImg, currentlyAnimatedElement);
        }
      }

      // Finální kontrola před zviditelněním
      if (operationIds.get(container) === operationId && visibilityState.get(container) === true) {
        img.style.opacity = '1';
      } else {
        img.style.opacity = '0';
      }
    } catch (error) {
      console.error('Error showing animated webp:', error);
    } finally {
      showQueue.delete(container);
    }
  })();

  showQueue.set(container, showPromise);
}

// ============================================================================
// HLAVNÍ FUNKCE
// ============================================================================

const createAnimatedWebpImage = (
  customConfig: Partial<AnimatedWebpConfig> = {}
): AnimatedWebpInstance => {
  let isMobile: boolean;
  let isLandscape: boolean;
  let currentlyAnimatedElement: HTMLElement | null = null;
  let animationFrameId: number | null = null;

  const config: AnimatedWebpConfig = { ...DEFAULT_CONFIG, ...customConfig };

  // -------------------- Aktualizace viditelného elementu --------------------

  const updateVisibleElement = (): void => {
    if (!isMobile || isLandscape) return;

    const containers = Array.from(document.querySelectorAll(SELECTORS.container)) as HTMLElement[];

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const closestContainer = findClosestContainer(
      containers,
      viewportWidth,
      viewportHeight,
      config
    );

    for (const container of containers) {
      const img = container.querySelector(SELECTORS.animatedWebp) as HTMLImageElement;
      if (!img) continue;

      if (container === closestContainer) {
        void showImage(img, container, currentlyAnimatedElement);
      } else {
        hideImage(img, container);
      }
    }

    currentlyAnimatedElement = closestContainer;
  };

  // -------------------- Mobile (loop) --------------------

  const initMobileLoop = (): void => {
    const loop = () => {
      updateVisibleElement();
      animationFrameId = requestAnimationFrame(loop);
    };
    animationFrameId = requestAnimationFrame(loop);
  };

  // -------------------- Desktop na hover --------------------

  const initHoverBehavior = (): void => {
    const containers = Array.from(document.querySelectorAll(SELECTORS.container)) as HTMLElement[];

    for (const container of containers) {
      const img = container.querySelector(SELECTORS.animatedWebp) as HTMLImageElement;
      if (!img) continue;

      const parentCard = container.closest(SELECTORS.parentCard) as HTMLElement | null;
      const targetElement = parentCard || container;

      if (!isMobile) {
        targetElement.addEventListener('mouseenter', () => {
          void showImage(img, container, currentlyAnimatedElement);
        });
        targetElement.addEventListener('focusin', () => {
          void showImage(img, container, currentlyAnimatedElement);
        });
        targetElement.addEventListener('mouseleave', () => {
          hideImage(img, container);
        });
        targetElement.addEventListener('focusout', () => {
          hideImage(img, container);
        });
      }
    }
  };

  // -------------------- Init --------------------

  const init = (): void => {
    isMobile = detectMobile();
    isLandscape = detectLandscape();

    initHoverBehavior();

    if (isMobile && !isLandscape) {
      initMobileLoop();
      updateVisibleElement();
    }
  };

  // -------------------- Úklid --------------------

  const destroy = (): void => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    conversionQueue.clear();
    showQueue.clear();

    const containers = Array.from(document.querySelectorAll(SELECTORS.container)) as HTMLElement[];

    for (const container of containers) {
      const img = container.querySelector(SELECTORS.animatedWebp) as HTMLImageElement;
      if (!img) continue;

      hideImage(img, container);

      // Uvolníme individuální blob URL pro tento container
      const blobUrl = containerBlobUrls.get(container);
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    }

    // Uvolníme všechny sdílené blobs
    sharedBlobCache.clear();

    currentlyAnimatedElement = null;
  };

  // -------------------- Aktualizace konfigurace --------------------

  const updateConfig = (newConfig: Partial<AnimatedWebpConfig>): void => {
    Object.assign(config, newConfig);
    updateVisibleElement();
  };

  init();

  return {
    destroy,
    updateConfig,
    updateVisibleElement,
  };
};

// ============================================================================
// EXPORTY
// ============================================================================

export const initAnimatedWebpImages = (): void => {
  createAnimatedWebpImage();
};

export default createAnimatedWebpImage;
